/**
 * 68.42: the planet table's globe projection (the owner's A, and his note: "in example the overlay isn't actually over
 * the planet") — the table's ground layer (ui/planetTableDraw.ts) draped on the terrain, and the upright marks' projection.
 *
 * The drape: the ground layer drawn in map terms into a canvas — its unit a screen pixel at the view's centre (`sppp`),
 * so every width, dash and ring keeps the flat map's size — over the fronts' tint; that canvas is the texture of a
 * grid mesh over the map square (±`extent` people about the theatre's centre) laid on the terrain: each vertex at the
 * ground's radius (core/surface.ts, the drawn coarse mesh's own interpolation) plus a lift of 0.4 % of the square's
 * width (the grid's chords between the mesh's vertices), polygon offset, depth-tested, no depth write. It takes the
 * globe's perspective, and the hills and the horizon hide it. The mesh is rebuilt only when the extent changes; the
 * texture only when the host asks (the scene changed).
 *
 * The upright marks (letters, plates, squads): a billboard at the ground point, hidden when the line from the eye to it
 * passes under the ground (sampled along it; the horizon and the hills alike).
 */
import * as THREE from 'three';
import type { PlanetState, Vec3 } from '../types';
import { getSurfaceRadius } from '../core/surface';
import { fromMap, type TopoFrame } from '../ui/topoMap';
import { drawTable, type M, type TableDrawOpts, type TableProj, type TableScene } from '../ui/planetTableDraw';

const hex = (c: string): [number, number, number] => { const n = parseInt(c.slice(1), 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; };
/** territory's contested band: a warm grey, neither side's */
const CONTESTED = [214, 200, 164];

export class PlanetTableDrape {
  readonly mesh: THREE.Mesh;
  private readonly canvas = document.createElement('canvas');
  private readonly texture: THREE.CanvasTexture;
  private extent = 0;
  /** the last draw's unit, so a host can tell when the view's scale has moved enough to redraw */
  sppp = 0;

  /** `frame` in people (its radius the planet's in people); `person` a person's height in world units; `origin` the planet's centre in the scene; `size` the texture's side. */
  constructor(private readonly planet: PlanetState, private readonly frame: TopoFrame, private readonly person: number, private readonly origin: THREE.Vector3, readonly size = 2048, anisotropy = 4) {
    this.canvas.width = this.canvas.height = size;
    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.colorSpace = THREE.SRGBColorSpace;
    this.texture.anisotropy = anisotropy;
    this.mesh = new THREE.Mesh(new THREE.BufferGeometry(), new THREE.MeshBasicMaterial({ map: this.texture, transparent: true, depthWrite: false, fog: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2, side: THREE.DoubleSide }));
    this.mesh.renderOrder = 1;
    this.mesh.frustumCulled = false;
  }

  /** The ground at map point `m`, `lift` people up, in the scene. */
  private ground(m: M, lift: number): THREE.Vector3 {
    const d = fromMap(this.frame, m.x, m.y);
    return new THREE.Vector3(d.x, d.y, d.z).multiplyScalar(getSurfaceRadius(this.planet, d) + lift * this.person).add(this.origin);
  }

  /** Lay the mesh over ±`extent` people (a `n` × `n` grid); nothing when the extent is unchanged. */
  setExtent(extent: number, n = 160): void {
    if (Math.abs(extent - this.extent) < 1e-6) return;
    this.extent = extent;
    const R = this.frame.radius, lift = Math.max(0.6, extent * 0.004), pos = new Float32Array((n + 1) * (n + 1) * 3), uv = new Float32Array((n + 1) * (n + 1) * 2), far = new Uint8Array((n + 1) * (n + 1)), idx: number[] = [];
    for (let j = 0; j <= n; j++) for (let i = 0; i <= n; i++) {
      const m = { x: -extent + (i / n) * 2 * extent, y: -extent + (j / n) * 2 * extent }, q = j * (n + 1) + i, w = this.ground(m, lift);
      far[q] = Math.hypot(m.x, m.y) > Math.PI * R * 0.97 ? 1 : 0;   // past the far side's pole the map folds: leave it out
      pos[q * 3] = w.x; pos[q * 3 + 1] = w.y; pos[q * 3 + 2] = w.z; uv[q * 2] = i / n; uv[q * 2 + 1] = j / n;
    }
    for (let j = 0; j < n; j++) for (let i = 0; i < n; i++) { const a = j * (n + 1) + i, b = a + 1, c = a + n + 1, d = c + 1; if (far[a] || far[b] || far[c] || far[d]) continue; idx.push(a, b, d, a, d, c); }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3)); geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2)); geo.setIndex(idx);
    this.mesh.geometry.dispose();
    this.mesh.geometry = geo;
  }

  /** Redraw the texture: the fronts' tint, then the scene's ground layer, `sppp` texture-free pixels a person. */
  draw(scene: TableScene, opts: TableDrawOpts, sppp: number): void {
    const E = this.extent, SZ = this.size, g = this.canvas.getContext('2d')!;
    this.sppp = sppp;
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.clearRect(0, 0, SZ, SZ);
    { // the fronts' tint (a coarse grid, smoothed up)
      const tn = 128, img = new ImageData(tn, tn), own = hex(scene.own), them = hex(scene.them);
      for (let j = 0; j < tn; j++) for (let i = 0; i < tn; i++) {
        const m = { x: -E + ((i + 0.5) / tn) * 2 * E, y: E - ((j + 0.5) / tn) * 2 * E }, t = Math.max(-1, Math.min(1, scene.control(m) * 1.4)), c = t > 0 ? own : them, q = (j * tn + i) * 4;
        const band = scene.contested ? Math.min(1, scene.contested(m) * 1.6) : 0;   // (territory's contested band: a neutral shade over the sides' tints)
        for (let c3 = 0; c3 < 3; c3++) img.data[q + c3] = c[c3] + (CONTESTED[c3] - c[c3]) * band;
        img.data[q + 3] = Math.max(Math.min(0.42, Math.abs(t) * 0.42), band * 0.4) * 255;
      }
      const tc = document.createElement('canvas'); tc.width = tc.height = tn; tc.getContext('2d')!.putImageData(img, 0, 0);
      g.imageSmoothingEnabled = true; g.drawImage(tc, 0, 0, SZ, SZ);
    }
    const k = SZ / (2 * E * sppp);
    g.setTransform(k, 0, 0, k, 0, 0);
    const P: TableProj = { at: m => ({ x: (m.x + E) * sppp, y: (E - m.y) * sppp, ok: Math.hypot(m.x, m.y) < Math.PI * this.frame.radius * 0.98 }), ppp: () => sppp };
    drawTable(g, P, scene, opts, 'ground');
    this.texture.needsUpdate = true;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
    this.texture.dispose();
  }
}

/**
 * The globe's projection for the upright marks: a map point to the screen (`w` × `h` px) at its ground, `ok` false
 * behind the camera or when the ground stands between the eye and it (sampled at `samples` points along the line).
 * `ppp` the screen's pixels a person there (across the view).
 */
export function globeProjection(camera: THREE.Camera, planet: PlanetState, frame: TopoFrame, person: number, origin: THREE.Vector3, w: number, h: number, samples = 20): TableProj {
  const v = new THREE.Vector3(), eye = camera.getWorldPosition(new THREE.Vector3()), q = new THREE.Vector3();
  const ground = (m: M, lift = 0): THREE.Vector3 => { const d = fromMap(frame, m.x, m.y); return new THREE.Vector3(d.x, d.y, d.z).multiplyScalar(getSurfaceRadius(planet, d) + lift * person).add(origin); };
  const hidden = (p: THREE.Vector3): boolean => {
    for (let i = 1; i < samples; i++) {
      q.lerpVectors(eye, p, i / samples).sub(origin);
      const r = q.length(); if (r < 1e-9) return true;
      const d: Vec3 = { x: q.x / r, y: q.y / r, z: q.z / r };
      if (r < getSurfaceRadius(planet, d) - 0.5 * person) return true;
    }
    return false;
  };
  return {
    at: m => {
      const p = ground(m, 0.5);
      v.copy(p).project(camera);
      const s = { x: (v.x * 0.5 + 0.5) * w, y: (-v.y * 0.5 + 0.5) * h, ok: v.z < 1 && v.z > -1 };
      return s.ok && hidden(p) ? { ...s, ok: false } : s;
    },
    ppp: m => { const a = ground(m), b = ground({ x: m.x + 1, y: m.y }); a.project(camera); b.project(camera); return Math.max(1e-4, Math.hypot((a.x - b.x) * w, (a.y - b.y) * h) / 2); },
  };
}
